import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { AlertCircle, CheckCircle, Download, Eye, Filter, Plus, RefreshCw, Search, XCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ErrorLog {
  id: string;
  error_code: string;
  error_message: string;
  error_context: Record<string, unknown>;
  stack_trace: string | null;
  user_id: string | null;
  severity: string;
  source: string | null;
  url: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

interface ErrorCatalogEntry {
  id: string;
  error_code: string;
  domain: string;
  severity: string;
  internal_message: string;
  user_message: string;
  resolution_steps: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function ErrorManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newError, setNewError] = useState({
    error_code: '',
    domain: 'database',
    severity: 'error',
    internal_message: '',
    user_message: '',
    resolution_steps: '',
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch error logs
  const { data: errorLogs, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['error-logs', searchQuery, severityFilter],
    queryFn: async () => {
      let query = supabase
        .from('error_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (searchQuery) {
        query = query.or(`error_code.ilike.%${searchQuery}%,error_message.ilike.%${searchQuery}%`);
      }

      if (severityFilter !== 'all') {
        query = query.eq('severity', severityFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ErrorLog[];
    },
  });

  // Fetch error catalog
  const { data: errorCatalog, isLoading: catalogLoading, refetch: refetchCatalog } = useQuery({
    queryKey: ['error-catalog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('error_catalog')
        .select('*')
        .order('error_code', { ascending: true });
      if (error) throw error;
      return data as ErrorCatalogEntry[];
    },
  });

  // Mark error as resolved
  const resolveErrorMutation = useMutation({
    mutationFn: async (logId: string) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('user_id', userData.user?.id)
        .single();

      const { error } = await supabase
        .from('error_logs')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: appUser?.id,
        })
        .eq('id', logId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-logs'] });
      toast({ title: 'Error marked as resolved' });
    },
  });

  // Add new error to catalog
  const addErrorMutation = useMutation({
    mutationFn: async (entry: typeof newError) => {
      const { error } = await supabase.from('error_catalog').insert(entry);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-catalog'] });
      setIsAddDialogOpen(false);
      setNewError({
        error_code: '',
        domain: 'database',
        severity: 'error',
        internal_message: '',
        user_message: '',
        resolution_steps: '',
      });
      toast({ title: 'Error code added to catalog' });
    },
    onError: (error) => {
      toast({ title: 'Failed to add error', description: error.message, variant: 'destructive' });
    },
  });

  // Export error logs
  const exportLogs = () => {
    if (!errorLogs) return;
    
    const csv = [
      ['Code', 'Message', 'Severity', 'Source', 'URL', 'Created At', 'Resolved At'].join(','),
      ...errorLogs.map(log => [
        log.error_code,
        `"${log.error_message.replace(/"/g, '""')}"`,
        log.severity,
        log.source || '',
        log.url || '',
        log.created_at,
        log.resolved_at || '',
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'error':
        return <Badge variant="destructive" className="bg-red-500">Error</Badge>;
      case 'warning':
        return <Badge variant="outline" className="border-amber-500 text-amber-500">Warning</Badge>;
      case 'info':
        return <Badge variant="secondary">Info</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getDomainBadge = (domain: string) => {
    const colors: Record<string, string> = {
      auth: 'bg-purple-100 text-purple-800',
      database: 'bg-blue-100 text-blue-800',
      candidate: 'bg-green-100 text-green-800',
      job: 'bg-amber-100 text-amber-800',
      communication: 'bg-pink-100 text-pink-800',
      integration: 'bg-cyan-100 text-cyan-800',
    };
    return <Badge className={colors[domain] || 'bg-gray-100 text-gray-800'}>{domain}</Badge>;
  };

  // Calculate stats
  const stats = {
    total: errorLogs?.length || 0,
    unresolved: errorLogs?.filter(l => !l.resolved_at).length || 0,
    critical: errorLogs?.filter(l => l.severity === 'critical' && !l.resolved_at).length || 0,
    today: errorLogs?.filter(l => {
      const today = new Date();
      const logDate = new Date(l.created_at);
      return logDate.toDateString() === today.toDateString();
    }).length || 0,
  };

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Error Management</h1>
          <p className="text-muted-foreground">
            Monitor application errors and manage error code definitions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Last 100 errors</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unresolved</CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.unresolved}</div>
              <p className="text-xs text-muted-foreground">Needs attention</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Critical</CardTitle>
              <AlertCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.critical}</div>
              <p className="text-xs text-muted-foreground">Unresolved critical</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Today</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.today}</div>
              <p className="text-xs text-muted-foreground">Errors today</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="logs" className="space-y-4">
          <TabsList>
            <TabsTrigger value="logs">Error Logs</TabsTrigger>
            <TabsTrigger value="catalog">Error Catalog</TabsTrigger>
          </TabsList>

          {/* Error Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Error Logs</CardTitle>
                    <CardDescription>View and manage application errors</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => refetchLogs()}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportLogs}>
                      <Download className="mr-2 h-4 w-4" />
                      Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by code or message..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={severityFilter} onValueChange={setSeverityFilter}>
                    <SelectTrigger className="w-[150px]">
                      <Filter className="mr-2 h-4 w-4" />
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Severities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Code</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead className="w-[100px]">Severity</TableHead>
                        <TableHead className="w-[150px]">Time</TableHead>
                        <TableHead className="w-[100px]">Status</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logsLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : errorLogs?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No errors found
                          </TableCell>
                        </TableRow>
                      ) : (
                        errorLogs?.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <code className="text-sm font-mono bg-muted px-1.5 py-0.5 rounded">
                                {log.error_code}
                              </code>
                            </TableCell>
                            <TableCell className="max-w-[300px] truncate">
                              {log.error_message}
                            </TableCell>
                            <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {format(new Date(log.created_at), 'MMM d, HH:mm')}
                            </TableCell>
                            <TableCell>
                              {log.resolved_at ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  Resolved
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-600">
                                  Open
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setSelectedLog(log)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {!log.resolved_at && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => resolveErrorMutation.mutate(log.id)}
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Error Catalog Tab */}
          <TabsContent value="catalog" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Error Code Catalog</CardTitle>
                    <CardDescription>Define and manage error codes and their messages</CardDescription>
                  </div>
                  <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Error Code
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Error Code</DialogTitle>
                        <DialogDescription>
                          Define a new error code with its messages and resolution steps
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="error_code">Error Code</Label>
                            <Input
                              id="error_code"
                              placeholder="E2010"
                              value={newError.error_code}
                              onChange={(e) => setNewError({ ...newError, error_code: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="domain">Domain</Label>
                            <Select
                              value={newError.domain}
                              onValueChange={(v) => setNewError({ ...newError, domain: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auth">Auth</SelectItem>
                                <SelectItem value="database">Database</SelectItem>
                                <SelectItem value="candidate">Candidate</SelectItem>
                                <SelectItem value="job">Job</SelectItem>
                                <SelectItem value="communication">Communication</SelectItem>
                                <SelectItem value="integration">Integration</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="severity">Severity</Label>
                          <Select
                            value={newError.severity}
                            onValueChange={(v) => setNewError({ ...newError, severity: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="info">Info</SelectItem>
                              <SelectItem value="warning">Warning</SelectItem>
                              <SelectItem value="error">Error</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="internal_message">Internal Message</Label>
                          <Input
                            id="internal_message"
                            placeholder="Technical description for developers"
                            value={newError.internal_message}
                            onChange={(e) => setNewError({ ...newError, internal_message: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user_message">User Message</Label>
                          <Input
                            id="user_message"
                            placeholder="Friendly message for users"
                            value={newError.user_message}
                            onChange={(e) => setNewError({ ...newError, user_message: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="resolution_steps">Resolution Steps</Label>
                          <Textarea
                            id="resolution_steps"
                            placeholder="Steps to resolve this error"
                            value={newError.resolution_steps}
                            onChange={(e) => setNewError({ ...newError, resolution_steps: e.target.value })}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={() => addErrorMutation.mutate(newError)}>
                          Add Error Code
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Code</TableHead>
                        <TableHead className="w-[120px]">Domain</TableHead>
                        <TableHead className="w-[100px]">Severity</TableHead>
                        <TableHead>Internal Message</TableHead>
                        <TableHead>User Message</TableHead>
                        <TableHead className="w-[80px]">Active</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : (
                        errorCatalog?.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>
                              <code className="text-sm font-mono bg-muted px-1.5 py-0.5 rounded">
                                {entry.error_code}
                              </code>
                            </TableCell>
                            <TableCell>{getDomainBadge(entry.domain)}</TableCell>
                            <TableCell>{getSeverityBadge(entry.severity)}</TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {entry.internal_message}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {entry.user_message}
                            </TableCell>
                            <TableCell>
                              {entry.is_active ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Error Detail Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <code className="text-lg font-mono bg-muted px-2 py-1 rounded">
                  {selectedLog?.error_code}
                </code>
                {selectedLog && getSeverityBadge(selectedLog.severity)}
              </DialogTitle>
              <DialogDescription>
                {selectedLog && format(new Date(selectedLog.created_at), 'PPpp')}
              </DialogDescription>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Error Message</Label>
                  <p className="text-sm mt-1">{selectedLog.error_message}</p>
                </div>
                
                {selectedLog.url && (
                  <div>
                    <Label className="text-sm font-medium">URL</Label>
                    <p className="text-sm mt-1 font-mono text-muted-foreground break-all">
                      {selectedLog.url}
                    </p>
                  </div>
                )}

                {selectedLog.error_context && Object.keys(selectedLog.error_context).length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Context</Label>
                    <ScrollArea className="h-[100px] mt-1">
                      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                        {JSON.stringify(selectedLog.error_context, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

                {selectedLog.stack_trace && (
                  <div>
                    <Label className="text-sm font-medium">Stack Trace</Label>
                    <ScrollArea className="h-[150px] mt-1">
                      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                        {selectedLog.stack_trace}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {selectedLog.resolved_at ? (
                      <span className="text-green-600">
                        Resolved on {format(new Date(selectedLog.resolved_at), 'PPp')}
                      </span>
                    ) : (
                      'Not yet resolved'
                    )}
                  </div>
                  {!selectedLog.resolved_at && (
                    <Button
                      onClick={() => {
                        resolveErrorMutation.mutate(selectedLog.id);
                        setSelectedLog(null);
                      }}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Mark as Resolved
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
}
